export const shouldCaptureErrorInSentry = (error: unknown): error is Error =>
  error instanceof Error &&
  !(
    error instanceof HttpMaintenanceError ||
    error instanceof HttpTooManyRequestsError ||
    error instanceof HttpBusinessConflictError ||
    error instanceof HttpNotFoundError ||
    error instanceof HttpForbiddenError ||
    error instanceof HttpUnauthenticatedError
  );

export const isTimeoutError = (error: Error) => error.name === 'TimeoutError'; // AbortSignal.timeout() triggered after n seconds
export const isAbortError = (error: Error) => error.name === 'AbortError'; // Fetch aborted by user action (browser stop button, closing tab, etc.
export const isFetchError = (error: Error) =>
  error instanceof TypeError &&
  (error.message === 'Failed to fetch' ||
    error.message === 'Load failed' ||
    error.message === 'NetworkError when attempting to fetch resource.'); // Network issue, CORS error, ...

export class FetchError extends Error {
  constructor(message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = 'FetchError';
  }
}

export class HttpError extends Error {
  public statusCode: number;

  constructor(statusCode: number, options?: ErrorOptions) {
    super(`HTTP error ${statusCode}`, options);
    // Chrome on iOS can swap in a wrapped `Error` that returns a plain error, which breaks `instanceof` for subclasses.
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = 'HttpError';
    this.statusCode = statusCode;
    Error.captureStackTrace?.(this, this.constructor);
  }
}

export class HttpServerError extends HttpError {
  constructor(statusCode = 500, options?: ErrorOptions) {
    super(statusCode, options);
    this.name = 'HttpServerError';
  }
}

export class HttpClientError extends HttpError {
  constructor(statusCode = 400, options?: ErrorOptions) {
    super(statusCode, options);
    this.name = 'HttpClientError';
  }
}

export class HttpMaintenanceError extends HttpServerError {
  constructor(options?: ErrorOptions) {
    super(503, options);
    this.name = 'HttpMaintenanceError';
  }
}

export class HttpUnauthenticatedError extends HttpClientError {
  constructor(options?: ErrorOptions) {
    super(401, options);
    this.name = 'HttpUnauthenticatedError';
  }
}

export class HttpForbiddenError extends HttpClientError {
  constructor(options?: ErrorOptions) {
    super(403, options);
    this.name = 'HttpForbiddenError';
  }
}

export class HttpNotFoundError extends HttpClientError {
  constructor(options?: ErrorOptions) {
    super(404, options);
    this.name = 'HttpNotFoundError';
  }
}

export class HttpBusinessConflictError extends HttpClientError {
  public errorCode: BusinessConflict;

  constructor(errorCode: BusinessConflict, options?: ErrorOptions) {
    super(409, options);
    this.name = 'HttpBusinessConflictError';
    this.errorCode = errorCode;
  }
}

export class HttpUnprocessableContentError extends HttpClientError {
  constructor(errors: unknown, options?: ErrorOptions) {
    super(422, options);
    this.name = 'HttpUnprocessableContentError';
    this.message = `${this.message}: ${JSON.stringify(errors)}`;
  }
}

export class HttpTooManyRequestsError extends HttpClientError {
  constructor(options?: ErrorOptions) {
    super(429, options);
    this.name = 'HttpTooManyRequestsError';
  }
}

type BusinessConflict =
  // Authentication
  | 'LoginInvalidEmailOrPassword'
  | 'IneligibleUserCannotLogIn'
  | 'RejectedUserCannotLogIn'
  | 'UserWithUnverifiedEmailCannotLogIn'
  | 'RejectedUserCannotResetPassword'
  | 'IneligibleUserCannotResetPassword'
  | 'UserWithUnverifiedEmailCannotResetPassword'
  | 'PasswordResetTokenDoesNotExist'

  // Settings
  | 'ForeignIban'
  | 'CurrentPasswordIsIncorrect'

  // Registration
  | 'ReferralCodeExpired'

  // Quotations
  | 'QuotationEntryNumberMustBeUnique'

  // Invoices
  | 'InvoiceEntryNumberMustBeUnique'
  | 'ApprovedInvoiceCannotBeLockedByUser'

  // Invitations
  | 'ExistingAccountCannotBeInvited'
  | 'ExistingUserCannotBeInvited';

export const httpErrorFromStatus = (statusCode: number): HttpError | null => {
  if (statusCode === 503) return new HttpMaintenanceError();
  if (statusCode >= 500) return new HttpServerError(statusCode);
  if (statusCode === 429) return new HttpTooManyRequestsError();
  if (statusCode === 404) return new HttpNotFoundError();
  if (statusCode === 403) return new HttpForbiddenError();
  if (statusCode === 401) return new HttpUnauthenticatedError();

  return null;
};
